import type { AnyActionSpec } from "@adea/actions/spec";
import { useEffect, useState } from "react";
import { PREFIXES, SPEC_FILES, type SpecFile } from "./specs.gen.ts";

/**
 * The web app's actions are loaded one spec file at a time, when a screen first asks for one of them: the first
 * screen should not carry the schemas of every screen it isn't showing. A call itself needs no spec (it is one
 * request); the spec adds the instant update and the exact "what did this touch" for the live channel, so the
 * client works with a stand-in until the real one arrives (see `actions.ts`).
 *
 * The files are every `*.specs.ts` in the web's packages, found by `scripts/discover.mjs` (`specs.gen.ts`), with the
 * files that define the actions starting with each word. An action whose word isn't there loads every file once:
 * slower, never wrong.
 */
type Specs = Record<string, AnyActionSpec>;

/** The specs a spec file exports (its other exports are shapes and helpers). */
function specsOf(module: Record<string, unknown>): Specs {
  const specs: Specs = {};
  for (const value of Object.values(module))
    if ((value as { __spec?: unknown } | null)?.__spec === true) {
      const spec = value as AnyActionSpec;
      specs[spec.name] = spec;
    }
  return specs;
}

/** What has arrived, by action name (each name is defined in one file). */
const loaded: Specs = {};
const files = new Map<SpecFile, Promise<void>>();
let everything: Promise<void> | undefined;
let everythingHere = false;

function loadFile(key: SpecFile): Promise<void> {
  let promise = files.get(key);
  if (!promise) {
    promise = SPEC_FILES[key]().then((module) => {
      Object.assign(loaded, specsOf(module as Record<string, unknown>));
    });
    files.set(key, promise);
  }
  return promise;
}

const soon = new Map<string, Promise<void>>();

/**
 * Like `ensureSpec`, but when the browser has nothing better to do (a moment after the screen is drawn): what a screen
 * names while it draws its actions arrives behind the screen, not beside it. The only things that wait are the instant
 * update of a change made in those first moments and the exact "what did this touch" of a read.
 */
export function ensureSpecSoon(name: string): Promise<void> {
  if (loaded[name]) return Promise.resolve();
  let promise = soon.get(name);
  if (!promise) {
    promise = new Promise<void>((resolve) => {
      const run = () => resolve(ensureSpec(name));
      if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 3000 });
      else setTimeout(run, 1500);
    });
    soon.set(name, promise);
  }
  return promise;
}

/** Every spec file (the palette lists every action). */
export function loadAllSpecs(): Promise<Record<string, AnyActionSpec>> {
  everything ??= Promise.all((Object.keys(SPEC_FILES) as SpecFile[]).map(loadFile)).then(() => {
    everythingHere = true;
  });
  return everything.then(() => loaded);
}

/** The spec of an action, once it has arrived. */
export const loadedSpec = (name: string): AnyActionSpec | undefined => loaded[name];

/**
 * Starts loading what `name` needs (nothing if it is already here) and resolves when it is. Safe to call on every
 * render: the work is done once per file.
 */
export function ensureSpec(name: string): Promise<void> {
  if (loaded[name]) return Promise.resolve();
  const keys = PREFIXES[name.split(".")[0] ?? ""];
  const first = keys
    ? Promise.all(keys.map(loadFile)).then(() => undefined)
    : loadAllSpecs().then(() => undefined);
  return first.then(() => (loaded[name] ? undefined : loadAllSpecs().then(() => undefined)));
}

/** Every action the web knows, once it has arrived (null until then): for the few places that list them all. */
export function useAllSpecs(): Record<string, AnyActionSpec> | null {
  // already here (the palette warms them when the browser is idle): no wait, no second render
  const [all, setAll] = useState<Record<string, AnyActionSpec> | null>(everythingHere ? loaded : null);
  useEffect(() => {
    if (!everythingHere) void loadAllSpecs().then(setAll);
  }, []);
  return all;
}
